import { MessageService, TreeNode } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { ChopMassnahme } from '@entity/chop';
import { Language, getLocalized } from '@entity/language';

/**
 * Builds a TreeNode forest from the flat TacsCodeKreis hierarchy.
 *
 * - Leaves marked `isMassnahme === true` are selectable.
 * - Non-Massnahme nodes are kept for navigation context but rendered as `selectable: false`.
 * - Branches that contain no selectable Massnahme are pruned to avoid clutter.
 * - Already-assigned ids (passed via `disabledIds`) are visible but rendered as `selectable: false`.
 * - When `visibleIds` is given, Massnahme leaves outside the set are omitted entirely.
 */
export function buildMassnahmenTree(
  nodes: ChopMassnahme[],
  language: Language,
  options: {
    disabledIds?: ReadonlySet<number>;
    visibleIds?: ReadonlySet<number>;
  } = {}
): TreeNode[] {
  const disabledIds = options.disabledIds ?? new Set<number>();
  const visibleIds = options.visibleIds;
  if (visibleIds) {
    nodes = nodes.filter((n) => !n.isMassnahme || visibleIds.has(n.id));
  }
  const treeNodes = new Map<number, TreeNode>();
  for (const node of nodes) {
    treeNodes.set(node.id, {
      key: String(node.id),
      label: `${node.tacsId} ${getLocalized(node.bezeichnung, language)}`,
      data: node,
      selectable: !disabledIds.has(node.id),
      children: [],
      expanded: true,
    });
  }

  const roots: TreeNode[] = [];
  for (const node of nodes) {
    const treeNode = treeNodes.get(node.id);
    if (!treeNode) {
      continue;
    }
    const parentTreeNode = node.supraId != null ? treeNodes.get(node.supraId) : undefined;
    if (parentTreeNode) {
      parentTreeNode.children!.push(treeNode);
    } else {
      roots.push(treeNode);
    }
  }

  return pruneEmptyBranches(roots);
}

function pruneEmptyBranches(nodes: TreeNode[]): TreeNode[] {
  return nodes
    .map((n) => ({ ...n, children: pruneEmptyBranches(n.children ?? []) }))
    .filter((n) => (n.data as ChopMassnahme).isMassnahme || (n.children?.length ?? 0) > 0);
}

export function currentLanguage(translateService: TranslateService): Language {
  const lang = translateService.getCurrentLang() ?? 'de';
  return (lang.substring(0, 1).toUpperCase() + lang.substring(1).toLowerCase()) as Language;
}

export function showTacsCodeKreisError(
  messageService: MessageService,
  translateService: TranslateService,
  err: { error?: { field?: string; error?: string; message?: string } }
): void {
  const field = err?.error?.field?.toLowerCase();
  const code = err?.error?.error;
  let key = 'GLOBAL.NOT_SUCCESSFULLY_SAVED';
  if (field === 'tacscodekreisid') {
    key = code === 'NotUnique' ? 'CHOP.ERROR_MASSNAHME_ALREADY_ASSIGNED' : 'CHOP.ERROR_TACSCODE_NOT_MASSNAHME';
  }
  messageService.add({
    severity: 'error',
    summary: translateService.instant(key),
    detail: field === 'tacscodekreisid' ? undefined : err?.error?.message,
  });
}
